<template>
  <el-form-item
    :label="name"
    :prop="name"
    :rules="rules"
  >
    <el-input-number
      ref="numberInput"
      v-model="val"
      type="text"
      :disabled="disabled"
      :precision="precision"
      :min="min"
      :max="max"
      :step="step"
      @input="input"
    />
  </el-form-item>
</template>

<script>
import { verify } from '@/utils/ik/verify/verify.js'

export default {
  name: 'IkFormNumber',
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    code: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    value: {
      type: Number,
      default: 0
    },
    edit: {
      type: Boolean,
      default: true
    },
    defaultValue: {
      type: Number,
      default: 0
    },
    precision: {
      type: Number,
      default: 2
    },
    step: {
      type: Number,
      default: 1
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100000
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tips: {
      type: String,
      default: ''
    },
    require: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: function() {
        return []
      }
    }
  },
  data() {
    return {
      val: 0,
      errorSign: false,
      message: '',
      minTooltip: null, // 存储最小值提示元素
      maxTooltip: null // 存储最大值提示元素
    }
  },
  computed: {
    getRequire() {
      return this.require
    }
  },
  watch: {
    value: {
      handler(value, oldValue) {
        if (value !== null && value !== '' && value !== undefined) {
          this.val = Number(value)
        } else {
          this.val = Number(this.defaultValue)
        }
        this.input(this.val)
      },
      immediate: true
    }
  },
  mounted() {
    this.addHoverListeners()
  },
  beforeDestroy() {
    this.removeHoverListeners()
    this.removeTooltips() // 确保销毁组件时移除所有提示
  },
  methods: {
    input(value) {
      this.message = verify(value, this.rules)
      this.errorSign = this.message !== null

      if (this.getRequire && this.isNull()) {
        this.errorSign = true
        this.message = "必填项，不可为空"
      }

      this.$emit('input', value)
    },
    isNull() {
      return this.val === '' || this.val === null || this.val === undefined
    },
    reset() {
      this.val = this.defaultValue
    },
    clean() {
      this.val = 0
    },
    addHoverListeners() {
      const inputElement = this.$refs.numberInput?.$el
      if (!inputElement) return

      // 查找减号按钮
      const decreaseBtn = inputElement.querySelector('.el-input-number__decrease')
      if (decreaseBtn) {
        decreaseBtn.addEventListener('mouseenter', this.showMinTooltip)
        decreaseBtn.addEventListener('mouseleave', this.hideMinTooltip)
      }

      // 查找加号按钮
      const increaseBtn = inputElement.querySelector('.el-input-number__increase')
      if (increaseBtn) {
        increaseBtn.addEventListener('mouseenter', this.showMaxTooltip)
        increaseBtn.addEventListener('mouseleave', this.hideMaxTooltip)
      }
    },
    removeHoverListeners() {
      const inputElement = this.$refs.numberInput?.$el
      if (!inputElement) return

      // 移除减号按钮的监听器
      const decreaseBtn = inputElement.querySelector('.el-input-number__decrease')
      if (decreaseBtn) {
        decreaseBtn.removeEventListener('mouseenter', this.showMinTooltip)
        decreaseBtn.removeEventListener('mouseleave', this.hideMinTooltip)
      }

      // 移除加号按钮的监听器
      const increaseBtn = inputElement.querySelector('.el-input-number__increase')
      if (increaseBtn) {
        increaseBtn.removeEventListener('mouseenter', this.showMaxTooltip)
        increaseBtn.removeEventListener('mouseleave', this.hideMaxTooltip)
      }
    },
    removeTooltips() {
      // 移除所有存在的提示
      if (this.minTooltip && this.minTooltip.parentNode) {
        this.minTooltip.parentNode.removeChild(this.minTooltip)
        this.minTooltip = null
      }
      if (this.maxTooltip && this.maxTooltip.parentNode) {
        this.maxTooltip.parentNode.removeChild(this.maxTooltip)
        this.maxTooltip = null
      }
    },
    showMinTooltip(event) {
      // 如果已有提示，先移除
      if (this.minTooltip && this.minTooltip.parentNode) {
        this.minTooltip.parentNode.removeChild(this.minTooltip)
      }

      // 创建提示元素
      this.minTooltip = document.createElement('div')
      this.minTooltip.className = 'ik-number-tooltip'
      this.minTooltip.textContent = `最小值: ${this.min}`

      // 设置样式
      this.minTooltip.style.position = 'absolute'
      this.minTooltip.style.zIndex = '9999'
      this.minTooltip.style.backgroundColor = '#303133'
      this.minTooltip.style.color = '#fff'
      this.minTooltip.style.padding = '8px'
      this.minTooltip.style.borderRadius = '4px'
      this.minTooltip.style.fontSize = '12px'
      this.minTooltip.style.boxShadow = '0 2px 12px 0 rgba(0, 0, 0, 0.1)'
      this.minTooltip.style.whiteSpace = 'nowrap'

      // 设置位置
      const rect = event.currentTarget.getBoundingClientRect()
      this.minTooltip.style.top = `${rect.top - 40}px`
      this.minTooltip.style.left = `${rect.left}px`

      // 添加到DOM
      document.body.appendChild(this.minTooltip)
    },
    hideMinTooltip() {
      // 移除提示元素
      if (this.minTooltip && this.minTooltip.parentNode) {
        this.minTooltip.parentNode.removeChild(this.minTooltip)
        this.minTooltip = null
      }
    },
    showMaxTooltip(event) {
      // 如果已有提示，先移除
      if (this.maxTooltip && this.maxTooltip.parentNode) {
        this.maxTooltip.parentNode.removeChild(this.maxTooltip)
      }

      // 创建提示元素
      this.maxTooltip = document.createElement('div')
      this.maxTooltip.className = 'ik-number-tooltip'
      this.maxTooltip.textContent = `最大值: ${this.max}`

      // 设置样式
      this.maxTooltip.style.position = 'absolute'
      this.maxTooltip.style.zIndex = '9999'
      this.maxTooltip.style.backgroundColor = '#303133'
      this.maxTooltip.style.color = '#fff'
      this.maxTooltip.style.padding = '8px'
      this.maxTooltip.style.borderRadius = '4px'
      this.maxTooltip.style.fontSize = '12px'
      this.maxTooltip.style.boxShadow = '0 2px 12px 0 rgba(0, 0, 0, 0.1)'
      this.maxTooltip.style.whiteSpace = 'nowrap'

      // 设置位置
      const rect = event.currentTarget.getBoundingClientRect()
      this.maxTooltip.style.top = `${rect.top - 40}px`
      this.maxTooltip.style.left = `${rect.left}px`

      // 添加到DOM
      document.body.appendChild(this.maxTooltip)
    },
    hideMaxTooltip() {
      // 移除提示元素
      if (this.maxTooltip && this.maxTooltip.parentNode) {
        this.maxTooltip.parentNode.removeChild(this.maxTooltip)
        this.maxTooltip = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep.IkFormNumber {
  .el-form-item {
    margin-bottom: 12px;
    width: 100%;
  }

  .el-input-number {
    width: 100%;
  }

  .el-form-item__label {
    width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .el-form-item__content {
    width: calc(100% - 180px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .el-input-number__decrease,
  .el-input-number__increase {
    position: relative;
    cursor: pointer;
  }
}

.IkFormNumber .el-input-number.item {
  width: calc(100% - 30px)
}

.ik-number-tooltip {
  position: fixed;
  z-index: 9999;
  background-color: #303133;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  white-space: nowrap;
}
</style>
